import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  CheckCircle2,
  Clock3,
  Database,
  Download,
  FlaskConical,
  History,
  Loader2,
  MessageSquare,
  RefreshCw,
  Save,
  Search,
  Square,
  Wrench,
  X,
} from 'lucide-react';
import {
  comparisonModelLabel,
  comparisonModelOptions,
  comparisonModelSelection,
  comparisonModelValue,
  comparisonSelectionReady,
  getResearchComparisonRun,
  listResearchComparisonRuns,
  omitHiddenModelData,
  openComparisonVariantInChat,
} from '../services/localResearchComparison';
import { useAppLayout } from './AppLayout';
import LegalMarkdown from './LegalMarkdown';
import { datumZeit } from '../utils/datum';
import { MAX_ACTIVE_COMPARISONS, useResearchComparison } from '../contexts/ResearchComparisonContext';
import { useRISViewer } from '../contexts/RISViewerContext';
import {
  answerLinkLabels,
  openViewerSource,
  readableSourceLinkLabel,
  resolveViewerSourceTarget,
  risReferenceLabel,
  sourceLabelKey,
  type ViewerSourceTarget,
} from '../services/risViewerLinks';
import {
  linkResolvedDocumentMarkdown,
  resolvableSourceUrls,
  resolvableUrlsInText,
  resolveDocumentSources,
  resolvedViewerTarget,
  type ResolvedDocumentMap,
} from '../services/documentSourceResolver';
import type {
  ComparisonModelCapabilities,
  ComparisonModelSelection,
  ResearchComparisonCapabilities,
  ResearchVariant,
  SavedResearchComparisonSummary,
  VariantEvaluation,
  VariantRunState,
} from '../types/localResearchComparison';

interface ResearchComparisonProps { capabilities: ResearchComparisonCapabilities }

const NOTES_MAX_LENGTH = 2_000;

/** Je Seite eine eigene Wahl; die Liste stammt ausschließlich vom Server. */
export const ComparisonModelSelector: React.FC<{
  variant: ResearchVariant;
  models: ComparisonModelCapabilities;
  selection?: ComparisonModelSelection;
  disabled: boolean;
  onChange: (selection: ComparisonModelSelection) => void;
}> = ({ variant, models, selection, disabled, onChange }) => {
  const options = comparisonModelOptions(models);
  const value = selection ? comparisonModelValue(selection) : 'legacy';
  return (
    <label className="block text-xs font-semibold text-primary" htmlFor={`comparison-model-${variant}`}>
      Modell auf Seite {variant}
      <select
        id={`comparison-model-${variant}`}
        value={value}
        disabled={disabled}
        onChange={event => {
          const next = comparisonModelSelection(event.target.value, models);
          if (next) onChange(next);
        }}
        className="rounded-[3px] mt-2 w-full border border-warm-200 bg-white px-3 py-2 text-sm font-normal text-primary focus:border-accent focus:outline-none disabled:opacity-60"
      >
        {!options.some(option => option.value === value) && (
          <option value={value} disabled>
            {selection ? `${comparisonModelLabel(selection)} (nicht verfügbar)`
              : variant === 'A' ? 'CLAiRIS Production (gespeichert)' : 'Gemini (Bahn B, gespeichert)'}
          </option>
        )}
        {options.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
      </select>
    </label>
  );
};

/** Der gewöhnliche Chat, das Ziel des Rückwegs aus dem Vergleich. */
export const CHAT_PATH = '/ris';

/**
 * Der Rückweg in den gewöhnlichen Chat. Der Vergleich ist eine Abzweigung des
 * Testbaus, keine Sackgasse: aus ihm führt sichtbar ein Weg zurück.
 */
export const BackToChatButton: React.FC = () => (
  <Link
    to={CHAT_PATH}
    className="rounded-[3px] flex items-center gap-2 border border-warm-200 bg-warm-card px-3 py-2 text-sm font-semibold text-primary hover:border-accent hover:bg-warm-200"
  >
    <MessageSquare className="h-4 w-4 text-accent" /> Zur Recherche
  </Link>
);


const formatDuration = (milliseconds: number | null | undefined): string => {
  if (milliseconds === null || milliseconds === undefined) return '–';
  if (milliseconds < 1000) return `${Math.round(milliseconds)} ms`;
  return `${(milliseconds / 1000).toFixed(milliseconds < 10000 ? 1 : 0)} s`;
};


const describeItem = (value: unknown): string => {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (!value || typeof value !== 'object' || Array.isArray(value)) return 'Provenienz erfasst';
  const record = value as Record<string, unknown>;
  const preferredKeys = ['title', 'name', 'label', 'reference', 'citation', 'court', 'technicalId', 'documentId', 'dokumentId', 'id', 'tool', 'url'];
  const parts = preferredKeys
    .map(key => record[key])
    .filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
    .slice(0, 2);
  return parts.length > 0 ? parts.join(' · ') : 'Provenienz erfasst';
};

const sourceProvenanceLabel = (value: unknown): string | null => ({
  retrieved: 'Abgerufen',
  declared: 'In Antwort angeführt',
  verified: 'Als verifiziert gemeldet',
}[typeof value === 'string' ? value : ''] ?? null);

// Namentlich ausgeführt, damit die Darstellung einer Quelle einzeln geprüft
// werden kann.
export const SourceItem: React.FC<{
  source: unknown;
  index: number;
  resolvedDocuments: ResolvedDocumentMap;
  answerLabels?: ReadonlyMap<string, string>;
  onOpenSource: (target: ViewerSourceTarget) => void;
}> = ({ source, index, resolvedDocuments, answerLabels, onOpenSource }) => {
  const record = source && typeof source === 'object' && !Array.isArray(source)
    ? source as Record<string, unknown>
    : {};
  const identifiers = record.identifiers && typeof record.identifiers === 'object' && !Array.isArray(record.identifiers)
    ? record.identifiers as Record<string, unknown>
    : {};
  // Eine Quelle, die nur als Adresse vorliegt, wird auf das Dokument des
  // eigenen Korpus umgelenkt, sobald die Auflösung vorliegt.
  const declaredTarget = resolveViewerSourceTarget(source);
  const resolution = resolvedViewerTarget(declaredTarget, resolvedDocuments);
  const target = resolution?.target ?? declaredTarget;
  const references = Array.isArray(record.references) ? record.references : [];
  const reference = [
    record.label,
    record.title,
    record.name,
    record.reference,
    record.citation,
    record.kennung,
    record.geschaeftszahl,
    ...(Array.isArray(record.geschaeftszahlen) ? record.geschaeftszahlen : []),
    ...references,
    identifiers.geschaeftszahl,
    identifiers.kennung,
    identifiers.technical_id,
  ]
    .filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
    .filter((item, itemIndex, all) => all.indexOf(item) === itemIndex)
    .slice(0, 3)
    .join(' · ');
  const collection = [record.collection, record.corpus]
    .find((item): item is string => typeof item === 'string' && item.trim().length > 0) ?? null;
  const context = [record.court, record.date, record.section, record.bahn]
    .filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
    .join(' · ');
  const label = sourceProvenanceLabel(record.provenanceType);
  const verification = record.found === true ? 'Bestätigt' : record.found === false ? 'Nicht bestätigt' : null;
  const primitiveLabel = typeof source === 'string'
    ? source.startsWith('/api/ris/document-viewer?')
      ? ''
      : source.replace(/https?:\/\/[^\s<>"']+/gi, '').replace(/^\s*\[\d+\]\s*/, '').replace(/[\[(]\s*[\])]\s*$/, '').trim()
    : '';
  const fallbackDescription = describeItem(source);
  // Die Antwort benennt dieselbe Fundstelle meist gehaltvoller ("§ 407 UGB")
  // als die aus der Adresse abgeleitete Beschriftung ("RIS-Dokument").
  const targetUrl = target.kind === 'ris' || target.kind === 'external'
    ? target.url
    : target.kind === 'document' ? target.externalUrl : null;
  const answerKey = targetUrl === null ? null : sourceLabelKey(targetUrl);
  const answerLabel = (answerKey === null ? undefined : answerLabels?.get(answerKey)) ?? '';
  const proposedLinkText = resolution?.resolvedDocument.title || reference || primitiveLabel
    || answerLabel
    || (target.kind === 'ris' ? risReferenceLabel(target.url) : '')
    || (target.kind === 'document' ? 'Dokument anzeigen' : '')
    || (fallbackDescription !== 'Provenienz erfasst' ? fallbackDescription : '')
    || (target.kind === 'external' ? target.url : '')
    || 'Quelldatensatz';
  const linkText = target.kind === 'external'
    ? readableSourceLinkLabel(proposedLinkText, target.url)
    : proposedLinkText;
  const externalUrl = target.kind === 'external'
    ? target.url
    : target.kind === 'document'
      ? target.externalUrl
      : null;

  return (
    <li className="break-words border-l border-warm-200 pl-2.5">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-muted">{index + 1}.</span>
        {target.kind === 'ris' ? (
          <a
            href={target.url}
            data-ris-preview="true"
            onClick={event => { event.preventDefault(); onOpenSource(target); }}
            rel="noopener noreferrer"
            className="font-medium text-primary underline decoration-accent/50 underline-offset-2 hover:text-accent-dark"
          >{linkText}</a>
        ) : target.kind === 'document' ? (
          <button
            type="button"
            data-document-preview={target.documentId}
            onClick={() => onOpenSource(target)}
            className="font-medium text-primary underline decoration-accent/50 underline-offset-2 hover:text-accent-dark"
          >{linkText}</button>
        ) : externalUrl ? (
          <a href={externalUrl} target="_blank" rel="noopener noreferrer" className="font-medium text-primary underline decoration-accent/50 underline-offset-2 hover:text-accent-dark">{linkText}</a>
        ) : (
          <span className="font-medium text-primary">{linkText}</span>
        )}
        {target.kind === 'document' && externalUrl && (
          <a href={externalUrl} target="_blank" rel="noopener noreferrer" className="text-[10px] text-muted underline underline-offset-2 hover:text-primary">Originalquelle</a>
        )}
        {label && <span className="rounded-[3px] border border-warm-200 bg-warm-100 px-1.5 py-0.5 text-[10px] text-muted">{label}</span>}
        {verification && <span className="rounded-[3px] border border-warm-200 bg-warm-100 px-1.5 py-0.5 text-[10px] text-muted">{verification}</span>}
      </div>
      {(collection || context) && <p className="mt-0.5 pl-5 text-[11px] text-muted">{[collection ? `Sammlung: ${collection}` : '', context].filter(Boolean).join(' · ')}</p>}
    </li>
  );
};

const reviewItems = (value: unknown): Array<{ label: string; text: string }> => {
  const values = Array.isArray(value) ? value : value === undefined || value === null ? [] : [value];
  return values.flatMap(item => {
    if (typeof item === 'string' && item.trim()) return [{ label: 'Prüfhinweis', text: item }];
    if (!item || typeof item !== 'object' || Array.isArray(item)) return [];
    const record = item as Record<string, unknown>;
    if (Array.isArray(record.objections)) {
      const objections = record.objections.flatMap(objection => reviewItems(objection));
      if (objections.length > 0) return objections;
      return record.status === 'certified' ? [{ label: 'Prüfhinweis', text: 'Die Abschlussprüfung meldet keine Einwände.' }] : [];
    }
    const origin = [record.origin, record.kind, record.type].find(entry => typeof entry === 'string');
    const label = origin === 'completion_review' ? 'Prüfhinweis' : 'Prüfhinweis';
    const claim = [record.claimText, record.text, record.message, record.objection, record.note, record.content]
      .find(entry => typeof entry === 'string' && entry.trim().length > 0);
    const reasoning = typeof record.reasoning === 'string' && record.reasoning.trim() ? record.reasoning : null;
    if (typeof claim === 'string') return [{ label, text: reasoning ? `${claim}\n\n${reasoning}` : claim }];
    try {
      return [{ label, text: JSON.stringify(omitHiddenModelData(record), null, 2) }];
    } catch {
      return [];
    }
  });
};

const extractToolRows = (value: unknown): unknown[] => {
  if (Array.isArray(value)) return value.flatMap(extractToolRows);
  if (!value || typeof value !== 'object') return value === undefined || value === null ? [] : [value];
  const record = value as Record<string, unknown>;
  if (Array.isArray(record.trace) && record.trace.length > 0) return record.trace.flatMap(extractToolRows);
  if (record.tools !== undefined && record.tools !== value) {
    const nested = extractToolRows(record.tools);
    if (nested.length > 0) return nested;
  }
  return [value];
};

const boundedJson = (value: unknown): string | null => {
  if (value === undefined) return null;
  try {
    const serialized = JSON.stringify(value, null, 2);
    return serialized.length > 1200 ? `${serialized.slice(0, 1200)}\n…` : serialized;
  } catch {
    return null;
  }
};

const ToolItem: React.FC<{ tool: unknown; index: number }> = ({ tool, index }) => {
  if (!tool || typeof tool !== 'object' || Array.isArray(tool)) {
    return <li className="break-words">{index + 1}. {describeItem(tool)}</li>;
  }
  const record = tool as Record<string, unknown>;
  const toolNames = Array.isArray(record.toolNames)
    ? record.toolNames.filter((item): item is string => typeof item === 'string')
    : [];
  const name = [record.toolName, record.name, record.tool]
    .find((item): item is string => typeof item === 'string' && item.trim().length > 0)
    ?? (toolNames.length > 0 ? toolNames.join(', ') : describeItem(tool));
  const details = boundedJson(record.params ?? record.arguments ?? record.input);

  return (
    <li className="break-words">
      {details ? (
        <details>
          <summary className="cursor-pointer font-medium text-primary">{index + 1}. {name}</summary>
          <pre className="rounded-[3px] mt-1 max-h-48 overflow-auto whitespace-pre-wrap border border-warm-200 bg-warm-100 p-2 text-[10px] leading-4 text-muted">{details}</pre>
        </details>
      ) : <span>{index + 1}. {name}</span>}
    </li>
  );
};

const describeProvenance = (value: unknown): string[] => {
  if (typeof value === 'string' && value.trim()) return [value];
  if (Array.isArray(value)) {
    const items = value.filter((item): item is string => typeof item === 'string' && item.trim().length > 0);
    return items.length > 0 ? [items.join(', ')] : [];
  }
  if (!value || typeof value !== 'object') return [];

  const labels: Record<string, string> = {
    configured: 'Konfiguriert',
    observed: 'Beobachtet',
    mode: 'Modus',
    name: 'Name',
    version: 'Version',
    source: 'Quelle',
  };
  return Object.entries(value as Record<string, unknown>)
    .flatMap(([key, item]) => {
      const label = labels[key] ?? key;
      if (item === null && key === 'observed') return [`${label}: noch nicht gemeldet`];
      if (typeof item === 'string' || typeof item === 'number' || typeof item === 'boolean') return [`${label}: ${String(item)}`];
      if (Array.isArray(item)) {
        const entries = item.filter(entry => typeof entry === 'string' || typeof entry === 'number');
        return entries.length > 0 ? [`${label}: ${entries.join(', ')}`] : [];
      }
      return [];
    })
    .slice(0, 6);
};

const statusLabel = (status: VariantRunState['status']): string => ({
  idle: 'Bereit',
  queued: 'Wartet',
  running: 'Läuft',
  done: 'Abgeschlossen',
  error: 'Fehlgeschlagen',
  cancelled: 'Abgebrochen',
}[status]);

const statusClasses = (status: VariantRunState['status']): string => {
  if (status === 'done') return 'border-green-200 bg-green-50 text-green-800';
  if (status === 'error') return 'border-red-200 bg-red-50 text-red-800';
  if (status === 'running') return 'border-accent/30 bg-accent/10 text-primary';
  return 'border-warm-200 bg-warm-100 text-muted';
};

const Rating: React.FC<{
  label: string;
  value: number | null;
  onChange: (value: number) => void;
  disabled?: boolean;
}> = ({ label, value, onChange, disabled = false }) => (
  <fieldset>
    <legend className="mb-1.5 text-xs font-medium text-primary">{label}</legend>
    <div className="flex gap-1" aria-label={`${label}, Bewertung von 1 bis 5`}>
      {[1, 2, 3, 4, 5].map(score => (
        <button
          key={score}
          type="button"
          onClick={() => onChange(score)}
          disabled={disabled}
          aria-pressed={value === score}
          className={`h-8 min-w-8 rounded border text-xs font-semibold transition-colors disabled:cursor-wait disabled:opacity-60 ${
            value === score
              ? 'border-primary bg-primary text-white'
              : 'border-warm-200 bg-warm-card text-muted hover:border-accent hover:text-primary'
          }`}
        >
          {score}
        </button>
      ))}
    </div>
  </fieldset>
);

// Namentlich ausgeführt, damit eine einzelne Karte geprüft werden kann.
export const VariantPanel: React.FC<{
  variant: ResearchVariant;
  capability: ResearchComparisonCapabilities['variants'][number] | undefined;
  state: VariantRunState;
  modelSelection?: ComparisonModelSelection;
  modelReady?: boolean;
  evaluation: VariantEvaluation;
  onEvaluationChange: (next: VariantEvaluation) => void;
  evaluationDisabled?: boolean;
  question: string;
  onOpenSource: (target: ViewerSourceTarget, context: { query: string; answer: string }) => void;
  /** Fehlt, solange der Lauf nicht gespeichert ist; dann gibt es nichts fortzusetzen. */
  onContinueInChat?: () => void;
  continuePending?: boolean;
  continueError?: string | null;
}> = ({
  variant,
  capability,
  state,
  modelSelection,
  modelReady,
  evaluation,
  onEvaluationChange,
  evaluationDisabled = false,
  question,
  onOpenSource,
  onContinueInChat,
  continuePending = false,
  continueError = null,
}) => {
  const sources = state.result?.sources ?? [];
  const verifiedSources = state.result?.verifiedSources ?? [];
  const reviews = reviewItems(state.result?.reviewAnnotation);
  const tools = state.tools;
  const toolRows = tools.flatMap(extractToolRows);
  const timings = state.result?.timings;
  const provenance = state.result?.provenance;
  const viewerContext = { query: question, answer: state.answer };

  // Quellen, die nur als Adresse vorliegen, werden je Lauf einmal auf
  // Dokumente des eigenen Korpus aufgelöst. Der Schlüssel enthält nur die
  // auflösbaren Adressen, damit sich der Effekt nicht bei jedem Lauf meldet.
  // Der Antworttext zählt erst mit, wenn der Lauf steht: eine halb übertragene
  // Adresse wäre eine andere Adresse.
  const externalSourceKey = resolvableSourceUrls([
    ...[...sources, ...verifiedSources]
      .map(source => resolveViewerSourceTarget(source))
      .flatMap(target => (target.kind === 'external' ? [target.url] : [])),
    ...(state.result ? resolvableUrlsInText(state.answer) : []),
  ]).join('\n');
  const [resolvedDocuments, setResolvedDocuments] = useState<ResolvedDocumentMap>(() => new Map());

  useEffect(() => {
    const urls = externalSourceKey ? externalSourceKey.split('\n') : [];
    if (urls.length === 0) {
      setResolvedDocuments(previous => (previous.size === 0 ? previous : new Map()));
      return;
    }
    let active = true;
    void resolveDocumentSources(urls).then(resolved => {
      if (active) setResolvedDocuments(resolved);
    });
    return () => { active = false; };
  }, [externalSourceKey]);

  // Aufgelöste Adressen im Antworttext werden zu Dokumentmarken, die die
  // Markdown-Darstellung bereits als Viewer-Verweis samt Originalquelle kennt.
  const answerMarkdown = useMemo(
    () => linkResolvedDocumentMarkdown(state.answer, resolvedDocuments),
    [state.answer, resolvedDocuments],
  );

  // Dieselbe Ableitung wie im Chat, damit Karte und Verlauf eine Quelle
  // gleich benennen: gelesen wird der Text, den die Karte darstellt. Die Karte
  // kennt keine gemeldeten Fundstellen, ihr vorbereiteter Text ist deshalb
  // allein die Auflösung der Dokumentadressen.
  const answerLabels = useMemo(() => answerLinkLabels(answerMarkdown), [answerMarkdown]);

  return (
    <article className="rounded min-w-0 border border-warm-200 bg-warm-card shadow-sm">
      <header className="border-b border-warm-200 px-4 py-4 sm:px-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="mb-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-accent">Variante {variant}</p>
            <h3 className="font-serif text-xl text-primary">{modelSelection || variant === 'B' ? 'CLAiRIS Staging' : 'CLAiRIS Production'}</h3>
            <p className="mt-1 text-xs text-muted">{modelSelection
              ? comparisonModelLabel(modelSelection)
              : variant === 'B' ? 'Live · Lane B' : 'Live · Aktueller Produktionsstand'}</p>
          </div>
          <span className={`rounded-full border px-2.5 py-1 text-xs font-medium ${statusClasses(state.status)}`}>
            {statusLabel(state.status)}
          </span>
        </div>
        {capability?.coverage && capability.coverage.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {capability.coverage.map(item => (
              <span key={item} className="rounded-sm border border-warm-200 bg-warm-100 px-2 py-1 text-[11px] text-muted">{item}</span>
            ))}
          </div>
        )}
      </header>

      <div className="space-y-5 p-4 sm:p-5">
        {!(modelReady ?? capability?.ready) && (
          <div className="rounded flex gap-2 border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{capability?.reason || 'Diese Variante meldet derzeit keine Bereitschaft.'}</span>
          </div>
        )}

        <section aria-label={`Antwort Variante ${variant}`}>
          <div className="mb-2 flex flex-wrap items-center justify-between gap-3">
            <h4 className="text-xs font-semibold uppercase tracking-[0.14em] text-muted">Antwort</h4>
            <div className="flex items-center gap-3">
              {state.elapsedMs !== null && <span className="text-xs tabular-nums text-muted">{formatDuration(state.elapsedMs)}</span>}
              {state.result && onContinueInChat && (
                <button
                  type="button"
                  disabled={continuePending}
                  onClick={onContinueInChat}
                  className="rounded-[3px] flex items-center gap-1.5 border border-warm-200 bg-warm-100 px-2.5 py-1 text-xs font-semibold text-primary hover:border-accent hover:text-accent-dark disabled:cursor-wait disabled:opacity-60"
                >
                  {continuePending
                    ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    : <MessageSquare className="h-3.5 w-3.5" />}
                  {continuePending ? 'Recherche wird geöffnet …' : 'Als Recherche fortsetzen'}
                </button>
              )}
            </div>
          </div>
          {continueError && (
            <p role="alert" className="mb-2 text-xs text-red-800">{continueError}</p>
          )}
          <div className="rounded min-h-44 border border-warm-200 bg-white/70 p-4">
            {state.error && (
              <div role="alert" className="mb-3 flex gap-2 text-sm text-red-800">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>
                  <strong>{state.error.code}:</strong> {state.error.message}
                  {state.error.diagnostic && (
                    <small className="mt-1 block font-mono text-[11px] text-red-700">
                      Fehlerreferenz {state.error.diagnostic.fingerprint}
                    </small>
                  )}
                </span>
              </div>
            )}
            {state.answer ? (
              <LegalMarkdown
                content={answerMarkdown}
                onOpenRisSource={url => onOpenSource({ kind: 'ris', url }, viewerContext)}
                onOpenDocumentSource={documentId => onOpenSource({ kind: 'document', documentId, externalUrl: null }, viewerContext)}
              />
            ) : !state.error && (
              <div className="flex min-h-32 items-center justify-center text-center text-sm text-muted">
                {state.status === 'running' || state.status === 'queued'
                  ? state.statusMessage || 'Recherche läuft …'
                  : 'Die Antwort erscheint nach dem Start hier.'}
              </div>
            )}
          </div>
        </section>

        {(state.status === 'running' || state.result) && (
          <section className="rounded grid grid-cols-3 gap-px overflow-hidden border border-warm-200 bg-warm-200" aria-label="Laufzeiten">
            {[
              ['Erster Status', timings?.firstStatusMs],
              ['Erster Text', timings?.firstAnswerMs],
              ['Gesamt', timings?.totalMs ?? state.elapsedMs],
            ].map(([label, value]) => (
              <div key={String(label)} className="bg-warm-100 px-2 py-3 text-center">
                <Clock3 className="mx-auto mb-1 h-3.5 w-3.5 text-accent" />
                <p className="text-[10px] uppercase tracking-wide text-muted">{label}</p>
                <p className="mt-1 text-xs font-semibold tabular-nums text-primary">{formatDuration(value as number | null | undefined)}</p>
              </div>
            ))}
          </section>
        )}

        {reviews.length > 0 && (
          <section className="rounded border border-amber-200 bg-amber-50 p-3" aria-label="Prüfhinweise">
            <h4 className="text-xs font-semibold uppercase tracking-[0.14em] text-amber-900">Prüfhinweise</h4>
            <ul className="mt-2 space-y-2 text-sm leading-6 text-amber-950">
              {reviews.map((review, index) => <li key={index}><span className="font-semibold">{review.label}:</span> <LegalMarkdown content={review.text} className="inline" /></li>)}
            </ul>
          </section>
        )}

        {(sources.length > 0 || verifiedSources.length > 0 || tools.length > 0 || provenance) && (
          <details open className="border-t border-warm-200 pt-4">
            <summary className="cursor-pointer text-sm font-semibold text-primary">Rechercheweg und Quellen</summary>
            <div className="mt-3 space-y-4 text-xs text-muted">
              {sources.length > 0 && (
                <div>
                  <p className="mb-2 flex items-center gap-1.5 font-semibold text-primary"><Database className="h-3.5 w-3.5" /> Quellen ({sources.length})</p>
                  <ul className="space-y-1.5">
                    {sources.map((source, index) => <SourceItem key={index} source={source} index={index} resolvedDocuments={resolvedDocuments} answerLabels={answerLabels} onOpenSource={target => onOpenSource(target, viewerContext)} />)}
                  </ul>
                </div>
              )}
              {verifiedSources.length > 0 && (
                <div>
                  <p className="mb-2 flex items-center gap-1.5 font-semibold text-primary"><CheckCircle2 className="h-3.5 w-3.5" /> Verifizierte Quellen ({verifiedSources.length})</p>
                  <ul className="space-y-1.5">
                    {verifiedSources.map((source, index) => <SourceItem key={index} source={source} index={index} resolvedDocuments={resolvedDocuments} answerLabels={answerLabels} onOpenSource={target => onOpenSource(target, viewerContext)} />)}
                  </ul>
                </div>
              )}
              {toolRows.length > 0 && (
                <div>
                  <p className="mb-2 flex items-center gap-1.5 font-semibold text-primary"><Wrench className="h-3.5 w-3.5" /> Werkzeugschritte ({toolRows.length})</p>
                  <ul className="space-y-1.5">
                    {toolRows.map((tool, index) => <ToolItem key={index} tool={tool} index={index} />)}
                  </ul>
                </div>
              )}
              {provenance && (
                <div>
                  <p className="font-semibold text-primary">Ausführung</p>
                  {describeProvenance(provenance.model).map((line, index) => <p key={`model-${index}`}>Modell · {line}</p>)}
                  {describeProvenance(provenance.corpus).map((line, index) => <p key={`corpus-${index}`}>Korpus · {line}</p>)}
                </div>
              )}
              {(state.result?.researchRunId || state.result?.metadata?.researchRunId) && (
                <div>
                  <p className="font-semibold text-primary">Lane-B-Nachweis</p>
                  <p className="mt-1 font-mono">{state.result.researchRunId ?? state.result.metadata?.researchRunId}</p>
                  <p className="mt-1">Zeitpunkt {state.result.metadata?.completedAt ?? 'im Laufprotokoll erfasst'}</p>
                </div>
              )}
            </div>
          </details>
        )}

        {state.status === 'done' && (
          <section className="border-t border-warm-200 pt-4" aria-label={`Bewertung Variante ${variant}`}>
            <h4 className="mb-3 font-serif text-lg text-primary">Eigene Bewertung</h4>
            <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-1 xl:grid-cols-3">
              <Rating label="Richtigkeit" value={evaluation.accuracy} disabled={evaluationDisabled} onChange={accuracy => onEvaluationChange({ ...evaluation, accuracy })} />
              <Rating label="Relevanz" value={evaluation.relevance} disabled={evaluationDisabled} onChange={relevance => onEvaluationChange({ ...evaluation, relevance })} />
              <Rating label="Quellen" value={evaluation.citations} disabled={evaluationDisabled} onChange={citations => onEvaluationChange({ ...evaluation, citations })} />
            </div>
            <label className="mt-4 block text-xs font-medium text-primary" htmlFor={`notes-${variant}`}>Beobachtungen</label>
            <textarea
              id={`notes-${variant}`}
              value={evaluation.notes}
              onChange={event => onEvaluationChange({ ...evaluation, notes: event.target.value })}
              maxLength={NOTES_MAX_LENGTH}
              disabled={evaluationDisabled}
              rows={3}
              placeholder="Was war überzeugend, unklar oder nicht belegt?"
              className="rounded-[3px] mt-1.5 w-full resize-y border border-warm-200 bg-white/70 px-3 py-2 text-sm text-primary placeholder:text-muted/70 focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20 disabled:cursor-wait disabled:opacity-60"
            />
            <p className="mt-1 text-right text-[10px] tabular-nums text-muted">{evaluation.notes.length}/{NOTES_MAX_LENGTH}</p>
          </section>
        )}
      </div>
    </article>
  );
};

const ResearchComparison: React.FC<ResearchComparisonProps> = ({ capabilities }) => {
  const { openViewer, openDocumentViewer, closeViewer } = useRISViewer();
  const {
    workspaces,
    activeWorkspace: workspace,
    activeCount,
    historyRevision,
    createDraft,
    selectWorkspace,
    updateDraft,
    startActive,
    cancelWorkspace,
    openSavedRun: putSavedRunInWorkspace,
    updateEvaluation,
    updatePreference,
    persistActiveFeedback,
  } = useResearchComparison();
  const [savedRuns, setSavedRuns] = useState<SavedResearchComparisonSummary[]>([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [openingRunId, setOpeningRunId] = useState<string | null>(null);
  const openingControllerRef = useRef<AbortController | null>(null);
  const viewerWorkspaceRef = useRef(workspace.clientId);

  const { question, selection, submittedQuestion, runId, startedAt, runError, runStorageState, isRunning, executedVariants, states, evaluations, preference, feedbackSaveState, feedbackSavedAt, feedbackSaveError } = workspace;

  useEffect(() => {
    if (viewerWorkspaceRef.current === workspace.clientId) return;
    viewerWorkspaceRef.current = workspace.clientId;
    closeViewer();
  }, [workspace.clientId, closeViewer]);

  const { onSessionSelect, triggerSidebarRefresh } = useAppLayout();
  const [continuingVariant, setContinuingVariant] = useState<ResearchVariant | null>(null);
  const [continueError, setContinueError] = useState<{ variant: ResearchVariant; message: string } | null>(null);

  // Aus einer fertigen Variante wird ein Chat: der Server legt ihn mit Frage
  // und Antwort an, geöffnet wird er wie eine Sitzung aus der Seitenleiste.
  const handleContinueInChat = useCallback(async (variant: ResearchVariant, savedRunId: string) => {
    setContinuingVariant(variant);
    setContinueError(null);
    try {
      await openComparisonVariantInChat(savedRunId, variant, onSessionSelect, {
        // Die Sitzung steht auf dem Server, bevor sie geöffnet wird; die
        // Seitenleiste lädt ihren Verlauf damit sofort nach.
        onSessionCreated: () => triggerSidebarRefresh(),
      });
    } catch (cause) {
      // Der Server begründet die Ablehnung deutsch; nur ohne Begründung
      // bleibt der allgemeine Satz.
      const message = cause instanceof Error && cause.message.trim().length > 0
        ? cause.message.trim()
        : 'Fortsetzen nicht möglich';
      setContinueError({ variant, message });
    } finally {
      // Auch der geglückte Weg endet hier: die Karte bleibt sonst wartend,
      // wenn die Navigation sie doch stehen lässt.
      setContinuingVariant(null);
    }
  }, [onSessionSelect, triggerSidebarRefresh]);

  const handleOpenSource = useCallback((
    target: ViewerSourceTarget,
    context: { query: string; answer: string },
  ) => {
    void openViewerSource(target, context, { openViewer, openDocumentViewer });
  }, [openViewer, openDocumentViewer]);

  const refreshSavedRuns = useCallback(async (signal?: AbortSignal) => {
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const runs = await listResearchComparisonRuns(signal);
      if (!signal?.aborted) setSavedRuns(runs);
    } catch (error) {
      if (signal?.aborted) return;
      if (!signal?.aborted) setHistoryError(error instanceof Error ? error.message : 'Gespeicherte Vergleiche konnten nicht geladen werden.');
    } finally {
      if (!signal?.aborted) setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void refreshSavedRuns(controller.signal);
    return () => controller.abort();
  }, [historyRevision, refreshSavedRuns]);

  useEffect(() => () => openingControllerRef.current?.abort(), []);

  const selectedVariants: ResearchVariant[] = selection === 'both' ? ['A', 'B'] : [selection];
  const displayedVariants = executedVariants.length > 0 ? executedVariants : selectedVariants;
  const selectedReady = selectedVariants.every(variant => comparisonSelectionReady(variant, workspace.models[variant], capabilities));
  const canStart = question.trim().length >= 10 && selectedReady && !submittedQuestion && !isRunning && activeCount < MAX_ACTIVE_COMPARISONS && openingRunId === null;

  const openSavedRun = async (targetRunId: string) => {
    if (targetRunId === runId || openingRunId) return;
    openingControllerRef.current?.abort();
    const controller = new AbortController();
    openingControllerRef.current = controller;
    setOpeningRunId(targetRunId);
    setHistoryError(null);
    try {
      const saved = await getResearchComparisonRun(targetRunId, controller.signal);
      if (!controller.signal.aborted) putSavedRunInWorkspace(saved);
    } catch (error) {
      if (!controller.signal.aborted) setHistoryError(error instanceof Error ? error.message : 'Der gespeicherte Vergleich konnte nicht geöffnet werden.');
    } finally {
      if (openingControllerRef.current === controller) {
        openingControllerRef.current = null;
        setOpeningRunId(null);
      }
    }
  };

  const startComparison = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canStart) return;
    void startActive(capabilities);
  };

  const cancel = () => cancelWorkspace(workspace.clientId);

  const downloadRecord = () => {
    const record = omitHiddenModelData({
      schema: 'risearch.local-comparison.v1',
      storage: {
        serverPersistence: runStorageState === 'confirmed',
        runStorageState,
        currentFeedbackSaved: runStorageState === 'confirmed' && (feedbackSaveState === 'idle' || feedbackSaveState === 'saved'),
        feedbackSaveState,
        feedbackSavedAt,
        export: 'browser-download',
      },
      exportedAt: new Date().toISOString(),
      runId,
      startedAt,
      userQuery: submittedQuestion,
      variants: executedVariants,
      models: workspace.executedModels,
      results: Object.fromEntries(executedVariants.map(variant => [variant, {
        label: workspace.executedModels[variant] ? comparisonModelLabel(workspace.executedModels[variant])
          : variant === 'A' ? 'CLAiRIS Production' : 'CLAiRIS Staging',
        status: states[variant].status,
        answer: states[variant].answer,
        sources: states[variant].result?.sources ?? [],
        verifiedSources: states[variant].result?.verifiedSources ?? [],
        reviewAnnotation: states[variant].result?.reviewAnnotation,
        researchRunId: states[variant].result?.researchRunId ?? states[variant].result?.metadata?.researchRunId,
        researchCompletion: states[variant].result?.researchCompletion ?? states[variant].result?.metadata?.researchCompletion,
        tools: states[variant].tools,
        timings: states[variant].result?.timings,
        provenance: states[variant].result?.provenance,
        error: states[variant].error,
        evaluation: evaluations[variant],
      }])),
      preference,
    });
    const blob = new Blob([JSON.stringify(record, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `clairis-vergleich-${runId || new Date().toISOString().slice(0, 10)}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="h-full overflow-y-auto bg-warm-bg text-primary">
      <div className="mx-auto max-w-[1500px] px-4 py-5 sm:px-6 lg:px-8">
        <header className="mb-6 border-b border-warm-200 pb-5">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="max-w-3xl">
              <p className="mb-2 flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-accent">
                <FlaskConical className="h-4 w-4" /> Forschungsvergleich
              </p>
              <h1 className="font-serif text-3xl leading-tight text-primary sm:text-4xl">Zwei Recherchewege. Eine unveränderte Frage.</h1>
              <p className="mt-3 max-w-2xl text-sm leading-6 text-muted">
                Wählen Sie für jede Seite unabhängig Gemini, das lokale Modell oder ein freigeschaltetes Cortecs-Modell. Jede Frage ist ein unabhängiger Einzellauf ohne vorherigen Chatverlauf.
              </p>
              {capabilities.executionMode === 'sequential' && (
                <p className="mt-2 max-w-2xl text-xs leading-5 text-muted">
                  Die Varianten werden nacheinander ausgeführt. Die angezeigte Laufzeit beginnt für jede Variante erst beim eigenen Start.
                </p>
              )}
              {capabilities.executionMode === 'parallel' && (
                <p className="mt-2 max-w-2xl text-xs leading-5 text-muted">
                  Die Varianten werden gleichzeitig ausgeführt. Die angezeigte Laufzeit beginnt für jede Variante beim eigenen Start.
                </p>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <BackToChatButton />
              <button type="button" onClick={createDraft} className="rounded-[3px] flex items-center gap-2 border border-warm-200 bg-warm-card px-3 py-2 text-sm font-semibold text-primary hover:border-accent hover:bg-warm-200">
                <X className="h-4 w-4 text-accent" /> Neuer Vergleich
              </button>
            </div>
          </div>
        </header>

        <section className="rounded mb-6 border border-warm-200 bg-warm-card p-4 shadow-sm" aria-labelledby="open-comparisons-heading">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 id="open-comparisons-heading" className="font-serif text-lg text-primary">Offene Vergleiche</h2>
              <p className="mt-1 text-xs text-muted">Bis zu {MAX_ACTIVE_COMPARISONS} Vergleiche laufen unabhängig weiter, während Sie zwischen ihnen wechseln.</p>
            </div>
            <span className="text-xs text-muted">{activeCount} aktiv</span>
          </div>
          <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
            {workspaces.map(item => (
              <button key={item.clientId} type="button" onClick={() => selectWorkspace(item.clientId)} aria-current={item.clientId === workspace.clientId ? 'true' : undefined} className={`rounded-[3px] min-w-48 max-w-72 border p-3 text-left ${item.clientId === workspace.clientId ? 'border-accent bg-accent/10' : 'border-warm-200 bg-warm-100 hover:border-accent'}`}>
                <span className="block truncate text-xs font-semibold text-primary">{item.submittedQuestion || item.question || 'Neuer Vergleich'}</span>
                <span className="mt-1 block text-[10px] text-muted">{item.isRunning ? 'Läuft im Hintergrund' : item.runId ? 'Gespeichert' : 'Entwurf'}</span>
              </button>
            ))}
          </div>
        </section>

        <section className="rounded mb-6 border border-warm-200 bg-warm-card p-4 shadow-sm" aria-labelledby="saved-comparisons-heading">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 id="saved-comparisons-heading" className="flex items-center gap-2 font-serif text-lg text-primary"><History className="h-4 w-4 text-accent" /> Gespeicherte Vergleiche</h2>
              <p className="mt-1 text-xs text-muted">Ergebnisse und Bewertungen Ihrer letzten Läufe können hier wieder geöffnet werden.</p>
            </div>
            <button type="button" onClick={() => { void refreshSavedRuns(); }} disabled={historyLoading} className="rounded-[3px] flex items-center gap-1.5 border border-warm-200 px-2.5 py-1.5 text-xs text-muted hover:border-accent hover:text-primary disabled:opacity-50">
              <RefreshCw className={`h-3.5 w-3.5 ${historyLoading ? 'animate-spin' : ''}`} /> Aktualisieren
            </button>
          </div>
          {historyError && <p role="alert" className="mt-3 text-xs text-red-700">{historyError}</p>}
          {historyLoading && savedRuns.length === 0 ? (
            <p className="mt-3 flex items-center gap-2 text-xs text-muted"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Gespeicherte Läufe werden geladen …</p>
          ) : savedRuns.length === 0 ? (
            <p className="mt-3 text-xs text-muted">Noch keine gespeicherten Vergleiche.</p>
          ) : (
            <div className="mt-3 grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
              {savedRuns.slice(0, 8).map(saved => (
                <button
                  key={saved.runId}
                  type="button"
                  onClick={() => { void openSavedRun(saved.runId); }}
                  disabled={openingRunId !== null}
                  aria-current={saved.runId === runId ? 'true' : undefined}
                  className={`rounded-[3px] min-w-0 border p-3 text-left disabled:cursor-not-allowed disabled:opacity-50 ${saved.runId === runId ? 'border-accent bg-accent/10' : 'border-warm-200 bg-warm-100 hover:border-accent'}`}
                >
                  <span className="block truncate text-xs font-semibold text-primary">{saved.userQuery}</span>
                  <span className="mt-1 flex items-center justify-between gap-2 text-[10px] text-muted">
                    <span>{datumZeit(saved.startedAt)}</span>
                    <span>{openingRunId === saved.runId ? 'Wird geöffnet …' : saved.feedbackUpdatedAt ? 'Bewertet' : 'Ohne Bewertung'}</span>
                  </span>
                </button>
              ))}
            </div>
          )}
        </section>

        <form onSubmit={startComparison} className="rounded mb-6 border border-warm-200 bg-warm-card p-4 shadow-sm sm:p-6">
          <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end">
            <div>
              <label htmlFor="comparison-question" className="font-serif text-xl text-primary">Vergleichsfrage</label>
              <p className="mt-1 text-xs text-muted">Der exakt gleiche Wortlaut wird ohne vorherige Nachrichten an jede ausgewählte Variante gesendet.</p>
              <textarea
                id="comparison-question"
                value={question}
                onChange={event => updateDraft({ question: event.target.value, selection })}
                rows={4}
                disabled={isRunning || Boolean(submittedQuestion)}
                placeholder="Formulieren Sie eine konkrete österreichische Rechtsfrage …"
                className="rounded-[3px] mt-3 w-full resize-y border border-warm-200 bg-white/70 px-4 py-3 text-sm leading-6 text-primary placeholder:text-muted/70 focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20 disabled:opacity-60"
              />
            </div>
            <div className="space-y-3 lg:w-72">
              <fieldset>
                <legend className="mb-2 text-xs font-semibold uppercase tracking-[0.12em] text-muted">
                  {isRunning ? 'Aktueller Lauf' : executedVariants.length > 0 ? 'Auswahl für nächsten Lauf' : 'Ausführung'}
                </legend>
                <div className="rounded-[3px] grid grid-cols-3 gap-1 border border-warm-200 bg-warm-100 p-1">
                  {([['both', 'Beide'], ['A', 'A'], ['B', 'B']] as const).map(([value, label]) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => updateDraft({ question, selection: value })}
                      disabled={isRunning || Boolean(submittedQuestion)}
                      aria-pressed={selection === value}
                      className={`rounded-sm px-3 py-2 text-xs font-semibold ${selection === value ? 'bg-primary text-white' : 'text-muted hover:text-primary'}`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </fieldset>
              {isRunning ? (
                <button type="button" onClick={cancel} className="rounded-[3px] flex w-full items-center justify-center gap-2 bg-red-700 px-4 py-3 text-sm font-semibold text-white hover:bg-red-800">
                  <Square className="h-3.5 w-3.5 fill-current" /> Vergleich abbrechen
                </button>
              ) : (
                submittedQuestion ? (
                  <button type="button" onClick={createDraft} className="rounded-[3px] flex w-full items-center justify-center gap-2 bg-primary px-4 py-3 text-sm font-semibold text-white hover:bg-primary-dark">
                    <Search className="h-4 w-4" /> Neuen Vergleich öffnen
                  </button>
                ) : (
                  <button type="submit" disabled={!canStart} className="rounded-[3px] flex w-full items-center justify-center gap-2 bg-primary px-4 py-3 text-sm font-semibold text-white hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-45">
                    <Search className="h-4 w-4" /> Vergleich starten
                  </button>
                )
              )}
            </div>
          </div>
          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            {(['A', 'B'] as const).map(variant => (
              <ComparisonModelSelector
                key={variant}
                variant={variant}
                models={capabilities.models}
                selection={workspace.models[variant]}
                disabled={isRunning || Boolean(submittedQuestion) || !selectedVariants.includes(variant)}
                onChange={model => updateDraft({ models: { ...workspace.models, [variant]: model } })}
              />
            ))}
          </div>
          {!selectedReady && (
            <p className="mt-3 flex items-center gap-2 text-xs text-amber-800"><AlertTriangle className="h-4 w-4" /> Mindestens eine ausgewählte Variante ist noch nicht bereit. Wählen Sie eine bereite Variante oder prüfen Sie die Laufzeitkonfiguration.</p>
          )}
          {activeCount >= MAX_ACTIVE_COMPARISONS && !isRunning && !submittedQuestion && (
            <p className="mt-3 flex items-center gap-2 text-xs text-amber-800"><AlertTriangle className="h-4 w-4" /> Es laufen bereits {MAX_ACTIVE_COMPARISONS} Vergleiche. Dieser Entwurf bleibt erhalten und kann danach gestartet werden.</p>
          )}
        </form>

        {submittedQuestion && (
          <section className="mb-5 border-l-2 border-accent bg-warm-100 px-4 py-3" aria-label="Unveränderte Testfrage">
            <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-accent">Unverändert gesendet</p>
            <p className="mt-1 whitespace-pre-wrap text-sm leading-6 text-primary">{submittedQuestion}</p>
          </section>
        )}

        {runError && (
          <div role="alert" className="rounded mb-5 flex gap-2 border border-red-200 bg-red-50 p-4 text-sm text-red-800">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> {runError}
          </div>
        )}

        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
          {displayedVariants.map(variant => (
            <VariantPanel
              key={variant}
              variant={variant}
              capability={capabilities.variants.find(item => item.variant === ((executedVariants.length > 0 ? workspace.executedModels : workspace.models)[variant] ? 'B' : variant))}
              modelSelection={(executedVariants.length > 0 ? workspace.executedModels : workspace.models)[variant]}
              modelReady={comparisonSelectionReady(variant, (executedVariants.length > 0 ? workspace.executedModels : workspace.models)[variant], capabilities)}
              state={states[variant]}
              evaluation={evaluations[variant]}
              evaluationDisabled={runStorageState !== 'confirmed' || feedbackSaveState === 'saving'}
              onEvaluationChange={next => updateEvaluation(variant, next)}
              question={submittedQuestion || question}
              onOpenSource={handleOpenSource}
              onContinueInChat={runId && runStorageState === 'confirmed'
                ? () => { void handleContinueInChat(variant, runId); }
                : undefined}
              continuePending={continuingVariant === variant}
              continueError={continueError?.variant === variant ? continueError.message : null}
            />
          ))}
        </div>

        {executedVariants.length === 2 && states.A.status === 'done' && states.B.status === 'done' && (
          <section className="rounded mt-6 border border-warm-200 bg-warm-card p-4 sm:p-6">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <h2 className="font-serif text-xl text-primary">Gesamteindruck</h2>
                <p className="mt-1 text-xs text-muted">Welche Antwort hilft bei dieser konkreten Frage mehr?</p>
              </div>
              <div className="flex flex-wrap gap-2">
                {([['A', 'Variante A'], ['B', 'Variante B'], ['tie', 'Gleichwertig']] as const).map(([value, label]) => (
                  <button key={value} type="button" disabled={runStorageState !== 'confirmed' || feedbackSaveState === 'saving'} aria-pressed={preference === value} onClick={() => updatePreference(value)} className={`rounded-[3px] border px-3 py-2 text-xs font-semibold disabled:cursor-wait disabled:opacity-60 ${preference === value ? 'border-primary bg-primary text-white' : 'border-warm-200 bg-warm-100 text-muted hover:border-accent hover:text-primary'}`}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </section>
        )}

        {runId && !isRunning && (
          <footer className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-warm-200 py-5">
            <div className="min-w-0 text-xs">
              <p className="flex items-center gap-2 text-muted">
                {runStorageState === 'unavailable' ? <AlertTriangle className="h-4 w-4 text-red-700" /> : runStorageState === 'pending' ? <Clock3 className="h-4 w-4 text-amber-700" /> : feedbackSaveState === 'saving' ? <Loader2 className="h-4 w-4 animate-spin text-accent" /> : feedbackSaveState === 'dirty' || feedbackSaveState === 'error' ? <AlertTriangle className="h-4 w-4 text-amber-700" /> : <CheckCircle2 className="h-4 w-4 text-accent" />}
                <span className="break-all">Lauf {runId}</span>
              </p>
              <p className={`mt-1 ${runStorageState === 'unavailable' || feedbackSaveState === 'error' ? 'text-red-700' : 'text-muted'}`} role={runStorageState === 'unavailable' || feedbackSaveState === 'error' ? 'alert' : undefined}>
                {runStorageState === 'unavailable' && 'Die Laufdaten konnten nicht in der Datenbank bestätigt werden. Dieser Export kann ungespeicherte Daten enthalten.'}
                {runStorageState === 'pending' && 'Der Speicherstatus dieses Laufs wurde nicht bestätigt.'}
                {runStorageState === 'confirmed' && feedbackSaveState === 'saving' && 'Bewertung wird in der Datenbank gespeichert …'}
                {runStorageState === 'confirmed' && feedbackSaveState === 'dirty' && 'Änderungen noch nicht gespeichert. Automatisches Speichern läuft gleich an.'}
                {runStorageState === 'confirmed' && feedbackSaveState === 'error' && (feedbackSaveError || 'Speichern fehlgeschlagen. Bitte erneut versuchen.')}
                {runStorageState === 'confirmed' && feedbackSaveState === 'saved' && `In der Datenbank gespeichert${feedbackSavedAt ? ` · ${datumZeit(feedbackSavedAt)}` : ''}.`}
                {runStorageState === 'confirmed' && feedbackSaveState === 'idle' && 'Die Laufdaten sind in der Datenbank gespeichert. Noch keine Bewertung abgegeben.'}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={() => { void persistActiveFeedback(); }} disabled={runStorageState !== 'confirmed' || feedbackSaveState === 'idle' || feedbackSaveState === 'saved' || feedbackSaveState === 'saving'} className="rounded-[3px] flex items-center gap-2 border border-primary bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-45">
                {feedbackSaveState === 'saving' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} {feedbackSaveState === 'error' ? 'Erneut speichern' : 'Bewertung speichern'}
              </button>
              <button type="button" onClick={downloadRecord} className="rounded-[3px] flex items-center gap-2 border border-primary px-4 py-2 text-sm font-semibold text-primary hover:bg-primary hover:text-white">
                <Download className="h-4 w-4" /> JSON herunterladen
              </button>
            </div>
          </footer>
        )}
      </div>
    </div>
  );
};

export default ResearchComparison;
